<script setup lang="ts">
import { Head, Link, usePage } from '@inertiajs/vue3';
import { computed, ref, watch } from 'vue';

import HarvestTrackerFooter from '../Components/HarvestTrackerFooter.vue';
import HarvestTrackerHeader from '../Components/HarvestTrackerHeader.vue';
import HarvestTrackerLogo from '../Components/HarvestTrackerLogo.vue';
import PrimaryButton from '../Components/PrimaryButton.vue';
import SecondaryButton from '../Components/SecondaryButton.vue';
import { useIsDesktop, useNavDirection } from '../composables/useNavDirection';

const props = defineProps<{
    title?: string;
}>();

const page = usePage();
const trackerSeason = computed(() => (page.props as Record<string, any>).tracker?.season as string | undefined);

// Mobile aside (flyout) state
const mobileAsideOpen = ref(false);

const { direction } = useNavDirection();
const isDesktop = useIsDesktop();

// Navigation depth: 0 = home, 1 = tracker, 2 = hunt
const depth = computed(() => {
    const props = page.props as Record<string, any>;
    if (props.hunt) return 2;
    if (props.tracker) return 1;
    return 0;
});

// topLevelDepth is 0 for home, 1 for any tracker-related pages. We key the
// main slot on this so the outer Transition only runs when entering/exiting
// tracker (home <-> tracker). The Hunt -> Show transitions remain handled
// by the inner HarvestDataLayout transitions.
const topLevelDepth = computed(() => (depth.value >= 1 ? 1 : 0));

const component = computed(() => page.component as string);

const isDeerActive = computed(() => component.value.startsWith('Deer/'));
const isSpringBearActive = computed(() => trackerSeason.value === 'spring');
const isFallBearActive = computed(() => trackerSeason.value === 'fall');
const isAnnualBearActive = computed(() => trackerSeason.value === 'annual');
const isMountainLionActive = computed(() => component.value.startsWith('MountainLion/'));

// Only the species Show/Hunt pages wrap their content in HarvestDataLayout's
// app-shell, which renders its own footer inside the scroll region. Every other
// page (Home, About, Resources, the annual bear table) gets the outer-panel footer.
const usesDataLayout = computed(() => /^(Deer|Bear|MountainLion)\/(Show|Hunt)$/.test(component.value));

// Close mobile aside when navigating or switching to desktop
watch(
    () => page.url,
    () => {
        mobileAsideOpen.value = false;
    },
);

watch(
    () => isDesktop.value,
    (v) => {
        if (v) mobileAsideOpen.value = false;
    },
);

// compute a page-level title that prefers an explicit `title` prop,
// otherwise derives it from the currently-selected sidebar item.
const pageTitle = computed(() => {
    if (props.title) return `${props.title} | AZGFD Harvest Tracker`;
    const comp = component.value || '';
    switch (true) {
        case comp.startsWith('Deer/'):
            return 'Archery Deer Tracking | AZGFD Harvest Tracker';
        case comp.startsWith('MountainLion/'):
            return 'Mountain Lion Tracking | AZGFD Harvest Tracker';
        case comp.startsWith('Bear/'): {
            const season = trackerSeason.value;
            if (season === 'spring') return 'Spring Bear Tracking | AZGFD Harvest Tracker';
            if (season === 'fall') return 'Fall Bear Tracking | AZGFD Harvest Tracker';
            if (season === 'annual') return 'Annual Bear Tracking | AZGFD Harvest Tracker';
        }
        default:
            return 'AZGFD Harvest Tracker';
    }
});
</script>

<template>
    <Head :title="pageTitle" />
    <div class="harvest-tracker-root relative min-h-screen items-stretch bg-(--az-beige-200) text-neutral-800 xl:flex">
        <HarvestTrackerHeader
            :mobileAsideOpen="mobileAsideOpen"
            :isDesktop="isDesktop"
            @update:mobileAsideOpen="(val: boolean) => (mobileAsideOpen = val)"
        />

        <!-- Shared aside: desktop in-flow, mobile flyout -->
        <div>
            <div v-if="mobileAsideOpen && !isDesktop" class="fixed inset-0 z-40" @click="mobileAsideOpen = false">
                <div class="absolute inset-0 bg-black/40"></div>
            </div>

            <Transition name="flyout">
                <aside
                    v-show="isDesktop || mobileAsideOpen"
                    :class="[
                        'flex h-full flex-1 shrink-0 flex-col gap-8 overflow-auto bg-(--az-light-beige-600) px-4 pt-4 pb-8 xl:min-w-sm xl:px-4.5 xl:py-8',
                        isDesktop
                            ? 'pointer-events-auto static w-full'
                            : 'pointer-events-auto fixed inset-y-0 right-0 w-72 transform shadow-xl transition-transform duration-200',
                        !isDesktop && !mobileAsideOpen ? 'translate-x-full' : 'translate-x-0',
                    ]"
                    style="z-index: 9999"
                >
                    <div class="hidden flex-col gap-4 px-4 py-5 xl:flex xl:p-0">
                        <Link
                            :href="route('harvest-tracker.home')"
                            class="text-2xl font-semibold text-gray-900"
                            aria-label="AZGFD Harvest Tracker home"
                        >
                            <HarvestTrackerLogo variant="stacked" class="w-auto text-[#212121]" />
                        </Link>
                    </div>

                    <div class="mx-auto flex h-full w-full max-w-xl flex-col justify-between gap-6">
                        <div>
                            <!-- Close button -->
                            <button
                                v-if="!isDesktop"
                                type="button"
                                aria-label="Close menu"
                                class="absolute top-4 right-4"
                                @click="mobileAsideOpen = false"
                            >
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M18 6L6 18" stroke="#212121" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                                    <path d="M6 6L18 18" stroke="#212121" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                                </svg>
                            </button>
                        </div>

                        <p class="font-bold xl:hidden">Select Harvest</p>

                        <div class="flex h-full flex-col gap-6">
                            <div class="flex h-full flex-col gap-6">
                                <SecondaryButton :href="route('harvest-tracker.deer.canonical')" :active="isDeerActive"
                                    >Archery Deer Tracking</SecondaryButton
                                >

                                <SecondaryButton :href="route('harvest-tracker.bear.spring.canonical')" :active="isSpringBearActive"
                                    >Spring Bear Tracking</SecondaryButton
                                >

                                <SecondaryButton :href="route('harvest-tracker.bear.fall.canonical')" :active="isFallBearActive"
                                    >Fall Bear Tracking</SecondaryButton
                                >

                                <SecondaryButton :href="route('harvest-tracker.bear.annual.canonical')" :active="isAnnualBearActive"
                                    >Annual Bear Tracking</SecondaryButton
                                >

                                <SecondaryButton :href="route('harvest-tracker.mountain-lion.canonical')" :active="isMountainLionActive"
                                    >Mountain Lion Tracking</SecondaryButton
                                >
                            </div>
                        </div>

                        <div class="flex h-full flex-col gap-10 xl:justify-end">
                            <div>
                                <PrimaryButton
                                    width="full"
                                    target="_blank"
                                    href="https://www.azgfd.com/hunting/hunt-draw-and-licenses/harvest-reporting/harvest-reporting-tracking/"
                                >
                                    <span class="">Report a Harvest</span>
                                    <span class="ml-2">
                                        <svg
                                            class="inline-block stroke-current"
                                            width="16"
                                            height="16"
                                            viewBox="0 0 16 16"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                d="M12 8.66667V12.6667C12 13.0203 11.8595 13.3594 11.6095 13.6095C11.3594 13.8595 11.0203 14 10.6667 14H3.33333C2.97971 14 2.64057 13.8595 2.39052 13.6095C2.14048 13.3594 2 13.0203 2 12.6667V5.33333C2 4.97971 2.14048 4.64057 2.39052 4.39052C2.64057 4.14048 2.97971 4 3.33333 4H7.33333"
                                                stroke="white"
                                                stroke-linecap="round"
                                                stroke-linejoin="round"
                                            />
                                            <path d="M10 2H14V6" stroke="white" stroke-linecap="round" stroke-linejoin="round" />
                                            <path d="M6.66669 9.33333L14 2" stroke="white" stroke-linecap="round" stroke-linejoin="round" />
                                        </svg>
                                    </span>
                                </PrimaryButton>
                            </div>

                            <div class="flex flex-col gap-8 text-lg">
                                <div class="flex items-center gap-2">
                                    <Link :href="route('harvest-tracker.resources')" class="hover:underline">Resources</Link>
                                </div>
                                <div class="flex gap-2">
                                    <Link :href="route('harvest-tracker.about')" class="hover:underline">About</Link>
                                </div>
                            </div>

                            <HarvestTrackerFooter />
                        </div>
                    </div>
                </aside>
            </Transition>
        </div>

        <Transition :name="`slide-${direction}`">
            <main :key="topLevelDepth" class="outer-panel flex w-full flex-col items-stretch">
                <slot />
                <HarvestTrackerFooter v-if="!usesDataLayout" class="shrink-0 border-t border-(--az-coffee-300) px-4 py-6 xl:hidden" />
            </main>
        </Transition>
    </div>
</template>
